"use client";

import type { GovernedNonAnswerable } from "@mlx/contracts";
import {
  type ColumnDef,
  useTable,
} from "@tanstack/react-table";
import {
  gridTableFeatures,
  type GridTableFeatures,
} from "@product/app/workspace/table-features";
import { Fragment, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

/**
 * The real refusal card and the real Markdown path, imported by source out of
 * `apps/app` — the same seam BRG-001 uses. See
 * `components/editorial/product-bridge`. The renderer pieces are the exact
 * modules `assistant-response-markdown.tsx` composes for every assistant turn:
 * the normalization pass, the remark/rehype plugin arrays and the shared
 * renderer components. The static map is that shared map minus one entry — the
 * live link renderer (BrandLink), which resolves plugin branding from the
 * runtime catalogue and so cannot cross into a static site. This specimen
 * contains no links, so nothing that renders here differs from the product.
 */
import { GovernedNonAnswerableCard } from "@product/components/app/runs/governed-non-answerable-card";
import { ArtifactCard } from "@product/components/app/runs/artifact-card";
import { RunSourcesUsedView } from "@product/components/app/runs/run-sources-used-view";
import {
  markdownRehypePlugins,
  markdownRemarkPlugins,
} from "@product/components/content/markdown-math";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { staticMarkdownRendererComponents } from "@product/components/content/markdown-renderer-components-static";
import { RecordTableGrid } from "@product/app/workspace/channels/[channelId]/record-table-grid";

import { DATA_LABEL_CLS, DISPLAY, LABEL_CLS, ProductBridge } from "@/components/editorial";

import type { ConceptGroup } from "../spec";
import {
  INVOICE_ROWS,
  type InvoiceRow,
  RECEIPT_PLUGINS,
  RECEIPT_SOURCES,
  RECEIVABLES_ANSWER_MARKDOWN,
  RECEIVABLES_ARTIFACTS,
  RECEIVABLES_QUESTION,
  RECEIVABLES_RECEIPT,
  REFUSAL_FIXTURE,
} from "./product-fixtures";

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

/* Ground inks for surface:"dark" sheets, matching the board's dark specs. */
const GINK_72 = "text-[color-mix(in_srgb,var(--theme-ground)_72%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";
const GINK_92 = "text-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)]";

/* ================================================================= */
/*  Reading the fixture                                               */
/* ================================================================= */

/**
 * SES-003 shows the same answer condensed. The cut happens at the Markdown
 * level — the aging table and the basis paragraph are dropped from the source
 * before it reaches the renderer — so what remains still renders through the
 * product pipeline untouched, and no figure is retyped.
 */
export const CONDENSED_ANSWER_MARKDOWN = (() => {
  const lines = RECEIVABLES_ANSWER_MARKDOWN.split("\n");
  const basisStart = lines.findIndex((line) => line.startsWith("Basis:"));
  return (basisStart < 0 ? lines : lines.slice(0, basisStart))
    .filter((line) => !line.trimStart().startsWith("|"))
    .join("\n");
})();

/* ================================================================= */
/*  Editorial framing                                                 */
/* ================================================================= */

/** Eyebrow, headline and standfirst — the claim, before the evidence. */
function Lede({
  eyebrow,
  headline,
  standfirst,
  dark = false,
}: {
  eyebrow: string;
  headline: string;
  standfirst: string;
  dark?: boolean;
}) {
  return (
    <header className="mb-8 max-w-[46rem]">
      <p className={`${dark ? GINK_72 : INK_72} ${LABEL_CLS} mb-4`}>{eyebrow}</p>
      <h2
        className={`${dark ? GINK_92 : INK_88} text-[1.65rem] leading-[1.18] font-semibold tracking-[-0.015em] sm:text-[2.1rem]`}
        style={DISPLAY}
      >
        {headline}
      </h2>
      <p className={`mt-4 max-w-[58ch] ${dark ? GINK_82 : INK_80} text-[1rem] leading-[1.6]`}>
        {standfirst}
      </p>
    </header>
  );
}

/** Exhibit header row — the DATA_LABEL pair over a dotted rule. */
function ExhibitHeader({
  left,
  right,
  dark = false,
}: {
  left: string;
  right: string;
  dark?: boolean;
}) {
  return (
    <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
      <span className={`${dark ? GINK_82 : INK_80} ${DATA_LABEL_CLS}`}>{left}</span>
      <strong
        className={`${dark ? "text-[var(--theme-accent)]" : "text-[var(--theme-accent-text)]"} ${DATA_LABEL_CLS}`}
      >
        {right}
      </strong>
    </div>
  );
}

/** Dotted footer row — where an exhibit records its provenance. */
function ExhibitFooter({
  children,
  dark = false,
}: {
  children: string;
  dark?: boolean;
}) {
  return (
    <p
      className={`border-t border-dotted border-[var(--rule)] px-4 py-[0.7rem] ${dark ? GINK_82 : INK_80} text-[0.72rem] leading-[1.45]`}
    >
      {children}
    </p>
  );
}

/* ================================================================= */
/*  Product reconstructions                                           */
/* ================================================================= */

/**
 * The user's turn. The product paints it with `.task-chat-bubble-user`
 * (`apps/app/src/app/globals.css`): 90% max width, right-aligned, 18px corners
 * with the 4px notch, `hsl(var(--chat-user-bg))`. That token is `40 16% 96%`
 * in the application's `:root` — the same triplet the bridge already carries on
 * `--card`, so the fill stays token-driven here instead of hard-coded.
 */
function QuestionBubble({ children }: { children: string }) {
  return (
    <div className="flex justify-end">
      <p
        className="m-0 w-fit max-w-[90%] text-sm leading-relaxed break-words"
        style={{
          background: "hsl(var(--card))",
          color: "hsl(var(--foreground))",
          borderRadius: "18px 18px 4px 18px",
          padding: "8px 14px",
        }}
      >
        {children}
      </p>
    </div>
  );
}

/**
 * The receipt row. The "Retrieved with" label and the overlapping source
 * avatars are the real `RunSourcesUsedView` — the presentational half of the
 * product's RunSourcesUsed, fed the run's sources and Xero's branding as
 * fixtures, with the product's own tooltips on hover. The printed source
 * names beside it and the dot-separated actions are the exhibit's: names
 * because a printed sheet has no hover, actions because in the product they
 * live in the transcript item's hover group.
 */
function ReceiptRow() {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex items-center gap-1.5">
        <RunSourcesUsedView sources={RECEIPT_SOURCES} plugins={RECEIPT_PLUGINS} />
        <span className="text-[11px] text-muted-foreground">
          {RECEIVABLES_RECEIPT.sources.map((source) => source.name).join(" · ")}
        </span>
      </div>
      <div className="-ml-1.5 flex min-h-7 items-center gap-1">
        {RECEIVABLES_RECEIPT.actions.map((action, index) => (
          <Fragment key={action}>
            {index > 0 ? (
              <span
                aria-hidden
                className="size-1 shrink-0 rounded-full bg-muted-foreground/40"
              />
            ) : null}
            <span className="px-1 text-xs text-muted-foreground">{action}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * The measure the answer is set to.
 *
 * The product caps an assistant turn at `max-w-[720px]`. This aging table
 * measures 735px, so in the application it scrolls the last column by fifteen
 * pixels — true, and unreadable as a printed exhibit. The specimen is set 48px
 * wider so the table prints whole; the flow, the ramp and the rules are
 * untouched, and below 48rem it scrolls exactly as the product does.
 */
const ANSWER_MEASURE = "max-w-[768px]";

/**
 * The governed answer, rendered by the product's own Markdown path. The
 * wrapper line is transcribed from `assistant-response-markdown.tsx` (only the
 * measure differs — see ANSWER_MEASURE); everything inside it is produced by
 * the application's modules: `normalizeMarkdownContent`, the shared
 * remark/rehype plugin arrays and the static renderer map, all imported from
 * `apps/app` rather than reimplemented. The GFM table therefore arrives with
 * the product's own `.typeset-scroll` wrapper and column alignment, and inline
 * code is the product's CodeBlock painting its `--content-code-*` tokens,
 * which the bridge now carries.
 */
function GovernedAnswer({ condensed = false }: { condensed?: boolean }) {
  return (
    <div
      className={`typeset typeset-chat ${ANSWER_MEASURE} break-words whitespace-normal`}
    >
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={markdownRehypePlugins}
        components={staticMarkdownRendererComponents}
      >
        {normalizeMarkdownContent(
          condensed ? CONDENSED_ANSWER_MARKDOWN : RECEIVABLES_ANSWER_MARKDOWN,
        )}
      </ReactMarkdown>
    </div>
  );
}

/* ================================================================= */
/*  SES-001 · A governed answer, in full                              */
/* ================================================================= */

export function GovernedAnswerExhibit({ dark = false, chrome = true }: { dark?: boolean; chrome?: boolean } = {}) {
  return (
    <div>
      {chrome ? (
        <Lede
        eyebrow={dark ? "Session · governed answer · after dark" : "Session · governed answer"}
        headline="Ask in a sentence. Read the whole working."
        standfirst="One question, one governed answer, the table, the totals and the reading of them. Underneath sits the receipt: every source the answer was retrieved with, and the way back into the trace that produced it."
      />
      ) : null}
      <figure className={`m-0 ${chrome ? "border border-[var(--rule)]" : ""}`}>
        {chrome ? (
          <ExhibitHeader
          left="Exhibit · one turn of a Session"
          right={dark ? "Real renderer · after dark" : "Real product renderer"}
        />
        ) : null}
        <ProductBridge appearance={dark ? "dark" : "light"} className={`px-4 py-6 sm:px-6 ${chrome ? "" : "border border-border"}`}>
          <div className={`mx-auto w-full ${ANSWER_MEASURE}`}>
            <QuestionBubble>{RECEIVABLES_QUESTION}</QuestionBubble>
            <div className="mt-5">
              <GovernedAnswer />
            </div>
            <ReceiptRow />
          </div>
        </ProductBridge>
        {chrome ? (
          <ExhibitFooter>
          {`The response is produced by the application's own Markdown path, its normalization pass, plugin set and renderer components, imported from apps/app rather than transcribed, set by the same imported typeset.css. Only the measure is altered, 768px against the product's 720px, so the aging table prints whole instead of scrolling its last column. Customer names are Xero's demo organisation.${dark ? " The appearance is the application's own dark blue on the canonical night, the asset keeps its dark while the section keeps the light ground." : ""}`}
        </ExhibitFooter>
        ) : null}
      </figure>
    </div>
  );
}

/* ================================================================= */
/*  SES-002 · When it will not answer                                 */
/* ================================================================= */

/**
 * Verbatim production strings from
 * `services/control-plane/src/governed_non_answerable.ts`. The card does not
 * render this object; the contract requires it.
 */
const CUSTOM_SQL_FALLBACK = {
  label: "Use custom SQL instead",
  warning:
    "Custom SQL is outside governed-contract guarantees. It may use a different business definition or grain even when the query is read-only.",
  confirmation:
    "I understand this answer will be ungoverned. Run only read-only, tenant-scoped SQL; preserve the SQL, sources, and provenance in the audit trace; and clearly label the result as ungoverned.",
} as const;

/** REFUSAL_FIXTURE, in the shape the real component takes. */
export const REFUSAL_OUTCOME: GovernedNonAnswerable = {
  kind: "unsupported",
  unansweredRequest: REFUSAL_FIXTURE.question,
  explanation: REFUSAL_FIXTURE.body,
  missingFields: [],
  suggestedFollowUp: REFUSAL_FIXTURE.suggestedFollowUp,
  customSqlFallback: CUSTOM_SQL_FALLBACK,
};

function RefusalExhibit() {
  const [pending, setPending] = useState(false);

  return (
    <div>
      <Lede
        eyebrow="Session · governed non-answerable"
        headline="Never an invented number."
        standfirst="Ask for something the published business context cannot support and MLX does not improvise. It names what it could not do, offers a question it can answer, and leaves the choice to look further with you."
      />
      <figure className="m-0 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit · the refusal, as it ships"
          right="Real product component"
        />
        {/* 720px: the card sets its own `max-w-[720px]`, so the question
            bubble's right edge lands on the card's. */}
        <ProductBridge className="px-4 py-6 sm:px-6">
          <div className="mx-auto w-full max-w-[720px]">
            <QuestionBubble>{REFUSAL_FIXTURE.question}</QuestionBubble>
            <div className="mt-5">
              <GovernedNonAnswerableCard
                outcome={REFUSAL_OUTCOME}
                customSqlExecution={{
                  isPending: pending,
                  onStart: () => {
                    setPending(true);
                    window.setTimeout(() => setPending(false), 1600);
                  },
                }}
              />
            </div>
          </div>
        </ProductBridge>
        <ExhibitFooter>{REFUSAL_FIXTURE.hardStop}</ExhibitFooter>
      </figure>
      <p className={`mt-4 max-w-[58ch] ${INK_72} text-[0.72rem] leading-[1.5]`}>
        Source: apps/app/src/components/app/governed-non-answerable-card.tsx, imported, not redrawn. The amber caution rule is the component's own colour, carried across unchanged.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  SES-003 · The answer becomes an artifact                          */
/* ================================================================= */

function ArtifactExhibit() {
  return (
    <div>
      <Lede
        eyebrow="Session · artifacts"
        headline="The work leaves the Session."
        standfirst="A governed answer is not something you have to copy out by hand. MLX writes it up as an artifact, a document or a spreadsheet, versioned and kept with the Session and the channel it belongs to."
      />
      <figure className="m-0 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit · the answer, and what it produced"
          right="Real product components"
        />
        <div className="grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <ProductBridge className="min-w-0 border-b border-dotted border-[var(--rule)] px-4 py-6 sm:px-6 md:border-r md:border-b-0">
            <GovernedAnswer condensed />
          </ProductBridge>
          <ProductBridge className="min-w-0 px-4 py-6 sm:px-6">
            <p className="mb-3 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
              Artifacts from this run
            </p>
            <div className="grid gap-3">
              {RECEIVABLES_ARTIFACTS.map((artifact) => (
                <ArtifactCard key={artifact.id} artifact={artifact} />
              ))}
            </div>
            <p className="mt-4 text-[0.72rem] leading-[1.5] text-muted-foreground">
              Each is written by the run that produced the answer, and stays with the Session and its channel.
            </p>
          </ProductBridge>
        </div>
        <ExhibitFooter>
          The artifact cards are the product's own ArtifactCard, imported from apps/app and fed the run's two artifacts as fixtures, the Created badge, the kind labels and the v1 are the component's own rendering.
        </ExhibitFooter>
      </figure>
    </div>
  );
}

/* ================================================================= */
/*  SES-004 · One number, end to end                                  */
/* ================================================================= */

/** Stage rail: numbered label left, the stage's own figure right. */
function StageLabel({
  index,
  title,
  note,
}: {
  index: string;
  title: string;
  note?: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <p className={`${INK_80} ${DATA_LABEL_CLS}`}>
        <span className="text-[var(--theme-accent-text)]">{index}</span>
        {" · "}
        {title}
      </p>
      {note ? (
        <p
          className={`${INK_72} text-[0.72rem] leading-[1.4] [font-variant-numeric:tabular-nums]`}
        >
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** The source ledger's columns — the real grid, four read-only fields. */
const INVOICE_COLUMNS: ColumnDef<GridTableFeatures, InvoiceRow>[] = [
  {
    id: "number",
    header: "Invoice",
    size: 96,
    cell: ({ row }) => (
      <span className="truncate font-medium text-foreground">
        {row.original.number}
      </span>
    ),
  },
  {
    id: "customer",
    header: "Customer",
    size: 216,
    cell: ({ row }) => (
      <span className="truncate text-foreground">{row.original.customer}</span>
    ),
  },
  {
    id: "dueDate",
    header: "Due date",
    size: 112,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">
        {row.original.dueDate}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    size: 132,
    cell: ({ row }) => (
      <span
        className={
          row.original.status === "Overdue"
            ? "truncate font-medium text-amber-700"
            : "truncate text-muted-foreground"
        }
      >
        {row.original.status}
      </span>
    ),
  },
  {
    id: "amountDue",
    header: "Amount due",
    size: 108,
    cell: ({ row }) => (
      <span className="truncate tabular-nums text-foreground">
        {row.original.amountDue}
      </span>
    ),
  },
];

/** The approved meaning between the ledger and the answer, as a plate. */
const DEFINITION_ROWS: [string, string][] = [
  ["Measure", "Aged receivables by customer"],
  ["Version", "v1 · approved"],
  [
    "Basis",
    "Xero sales invoices with a positive live AmountDue; voided and deleted invoices excluded",
  ],
  ["Aging", "By invoice due date, as at the synced snapshot"],
  ["Unsupported", "Forward projections, no approved basis exists"],
];

/**
 * The whole causal chain on one sheet: the ledger MLX read, the definition a
 * person approved, the governed answer they produce — the same total at every
 * stage — and the boundary where the run would stop instead of guessing.
 */
function EndToEndExhibit() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const table = useTable({
    features: gridTableFeatures,
    data: INVOICE_ROWS,
    columns: INVOICE_COLUMNS,
    enableColumnResizing: false,
  });

  return (
    <div>
      <Lede
        eyebrow="Session · end to end"
        headline="One number, all the way down."
        standfirst="Every governed figure can be walked back to the ledger it came from. Sixteen synced invoices, one approved definition, one governed answer carrying the same £21,541.38, and the stated boundary where MLX stops rather than guesses."
      />
      <figure className="m-0 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit · from ledger to governed answer"
          right="Real product components"
        />

        <div className="border-b border-dotted border-[var(--rule)] px-4 py-5 sm:px-6">
          <StageLabel
            index="01"
            title="What it read, Xero sales invoices, read-only"
            note="16 invoices · £21,541.38 outstanding"
          />
          <ProductBridge className="mt-3">
            <div className="flex min-w-0 flex-col">
              <RecordTableGrid
                activeCell={null}
                editingCell={null}
                canEditCell={() => false}
                editOnClick={() => false}
                onSelectCell={() => {}}
                onEditCell={() => {}} table={table} scrollRef={scrollRef} empty={false} />
            </div>
          </ProductBridge>
        </div>

        <div className="grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="border-b border-dotted border-[var(--rule)] px-4 py-5 sm:px-6 md:border-r md:border-b-0">
            <StageLabel
              index="02"
              title="What was approved"
              note="Reviewed by your team"
            />
            <dl className="m-0 mt-3 border border-dotted border-[var(--rule)]">
              {DEFINITION_ROWS.map(([label, value], index) => (
                <div
                  key={label}
                  className={`grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 px-3 py-2 ${
                    index > 0 ? "border-t border-dotted border-[var(--rule)]" : ""
                  }`}
                >
                  <dt className={`${INK_72} ${DATA_LABEL_CLS}`}>{label}</dt>
                  <dd className={`m-0 ${INK_88} text-[0.8rem] leading-[1.5]`}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
            <p className={`mt-3 ${INK_72} text-[0.72rem] leading-[1.5]`}>
              The definition is what turns rows into a measure: same ledger, same
              meaning, every time it is asked.
            </p>
          </div>
          <div className="min-w-0 px-4 py-5 sm:px-6">
            <StageLabel
              index="03"
              title="What it answered"
              note="The same £21,541.38"
            />
            <ProductBridge className="mt-3 px-4 py-5">
              <QuestionBubble>{RECEIVABLES_QUESTION}</QuestionBubble>
              <div className="mt-4">
                <GovernedAnswer condensed />
              </div>
              <ReceiptRow />
            </ProductBridge>
          </div>
        </div>

        <p
          className={`border-t border-dotted border-[var(--rule)] px-4 py-3 sm:px-6 ${INK_80} text-[0.78rem] leading-[1.55]`}
        >
          <span className={`${DATA_LABEL_CLS} text-[var(--theme-accent-text)]`}>
            04 · Where it stops
          </span>{" "}
          , “{REFUSAL_FIXTURE.question}” asks for a forward projection outside the
          approved basis. {REFUSAL_FIXTURE.hardStop} SES-002 shows that refusal as
          it ships.
        </p>

        <ExhibitFooter>
          The grid, the answer typography and the receipt avatars are the product's own components imported from apps/app; the definition plate is the board's drawing, the published-context surface is not yet importable. Invoice-level splits of a customer's aging bucket are illustrative; counts, totals and buckets reconcile exactly with the recorded run. Customer names are Xero's demo organisation.
        </ExhibitFooter>
      </figure>
    </div>
  );
}

/* ================================================================= */

export const SESSION_GROUP: ConceptGroup = {
  id: "sessions",
  prefix: "SES",
  title: "Agent sessions",
  blurb:
    "Marketing compositions built on the Session surface, on the application's own components imported through the bridge: the Markdown pipeline and typeset render the answer, the refusal card, the receipt avatars and the artifact cards are the product's, and the end-to-end sheet walks one figure from the source ledger to the governed answer.",
  specs: [
    {
      id: "SES-001",
      title: "A governed answer, in full",
      note: "The whole turn: the question as the product renders it, the answer through the real renderer and typeset, and the receipt row beneath it.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Governed answer",
      render: () => <GovernedAnswerExhibit />,
    },
    {
      id: "SES-002",
      title: "When it will not answer",
      note: "The real GovernedNonAnswerableCard on a question the published business context cannot support, with the hard-stop line as the footnote.",
      surface: "ground",
      width: "contained",
      interaction: "prototype",
      state: "Governed non-answerable",
      render: () => <RefusalExhibit />,
    },
    {
      id: "SES-003",
      title: "The answer becomes an artifact",
      note: "The same governed answer condensed, beside the real ArtifactCards for the document and spreadsheet it produced, the work leaving the Session as something to keep.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Artifacts",
      render: () => <ArtifactExhibit />,
    },
    {
      id: "SES-005",
      title: "A governed answer, after dark",
      note: "SES-001's full turn with the asset after dark, real renderer, typeset, receipt and inline-code tokens in their dark values, the night plate floating in the light marketing section.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "After dark",
      variantOf: "SES-001",
      render: () => <GovernedAnswerExhibit dark />,
    },
    {
      id: "SES-004",
      title: "One number, end to end",
      note: "Sixteen synced invoices in the real grid, the approved definition, and the governed answer carrying the same total, with the boundary stated where the run would stop.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "End to end",
      render: () => <EndToEndExhibit />,
    },
  ],
};
